import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, Alert, Snackbar } from '@mui/material';
import Layout from './components/Layout';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import DashboardV2 from './pages/DashboardV2';
import OrganizationOverview from './pages/OrganizationOverview';
import Users from './pages/Users';
import Organizations from './pages/Organizations';
import Companies from './pages/Companies';
import Accounts from './pages/Accounts';
import Tasks from './pages/Tasks';
import Timesheets from './pages/Timesheets';
import Subscriptions from './pages/Subscriptions';
import AdminSubscriptions from './pages/AdminSubscriptions';
import AdminStripeSync from './pages/AdminStripeSync';
import AdminReferenceFx from './pages/AdminReferenceFx';
import AdminEmailService from './pages/AdminEmailService';
import Issues from './pages/Issues';
import Metrics from './pages/Metrics';
import OrganizationDashboard from './pages/OrganizationDashboard';
import OrganizationSettings from './pages/OrganizationSettings';
import Approvals from './pages/Approvals';
import UserPreferences from './pages/UserPreferences';
import AcceptInvitation from './pages/AcceptInvitation';
import NoOrganization from './pages/NoOrganization';
import Help from './pages/Help';
import Manage from './pages/Manage';
import Report from './pages/Report';
import AddCompany from './pages/AddCompany';
import AddAccount from './pages/AddAccount';
import api from './services/api';
import { UserPreferencesProvider } from './hooks/useUserPreferences';
import { firebaseAnalytics } from './services/firebaseAnalytics';
import VerifyEmail from './components/VerifyEmail';
import CreateAccount from './pages/CreateAccount';
import DownloadWindows from './pages/DownloadWindows';

interface User {
  id: string;
  email: string;
  displayName?: string;
  isVerified: boolean;
  isActive: boolean;
  systemRole?: string;
}

// TimeSentinel Light Mode Color Palette Theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0078D4', // CharcoalAccentBrush - Microsoft Blue
      light: '#1F9CF0', // PrimaryMain - Light Blue for selected items
      dark: '#004578', // CharcoalAccentPressedBrush - Darkest Blue
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#C0C0C0', // CharcoalMediumBrush - for borders and secondary elements
      light: '#D0D0D0', // CharcoalLightBrush - for section backgrounds
      dark: '#E0E0E0', // CharcoalDarkerBrush - for main window background
      contrastText: '#000000',
    },
    background: {
      default: '#FAFAFA', // Much lighter gray for main window background
      paper: '#FFFFFF', // White for main content area
    },
    text: {
      primary: '#000000', // TextPrimaryBrush - Black for maximum contrast
      secondary: '#333333', // Darker gray for better readability
    },
    success: {
      main: '#107C10', // SuccessBrush
      contrastText: '#FFFFFF',
    },
    warning: {
      main: '#D83B01', // WarningBrush
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#E81123', // ErrorBrush
      contrastText: '#FFFFFF',
    },
    divider: '#C0C0C0', // CharcoalMediumBrush
    action: {
      hover: '#005A9F', // CharcoalAccentHoverBrush
      selected: '#1F9CF0', // PrimaryMain
      disabled: '#C0C0C0', // CharcoalMediumBrush
      disabledBackground: '#C0C0C0',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontSize: '14px',
      fontWeight: 700, // Bold for section titles
    },
    body1: {
      fontSize: '13px',
    },
    body2: {
      fontSize: '11px',
      color: '#333333', // Darker gray for better readability
    },
    button: {
      fontSize: '13px',
      fontWeight: 500,
      textTransform: 'none', // Don't uppercase buttons
    },
  },
  shape: {
    borderRadius: 8, // 8px for section containers
  },
  components: {
    // Primary Button - SettingsActionButton style
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          height: '32px',
          padding: '8px 16px',
        },
        containedPrimary: {
          backgroundColor: '#0078D4', // CharcoalAccentBrush
          color: '#FFFFFF !important', // White text for maximum contrast
          border: 'none',
          '&:hover': {
            backgroundColor: '#005A9F', // CharcoalAccentHoverBrush
            color: '#FFFFFF !important', // Ensure white text on hover
          },
          '&:active': {
            backgroundColor: '#004578', // CharcoalAccentPressedBrush
            color: '#FFFFFF !important', // Ensure white text when active
          },
          '&:disabled': {
            backgroundColor: '#C0C0C0', // CharcoalMediumBrush
            color: '#333333', // Darker gray for disabled state
          },
        },
        outlined: {
          backgroundColor: 'transparent',
          color: '#000000', // TextPrimaryBrush
          borderColor: '#C0C0C0', // CharcoalMediumBrush
          borderWidth: '1px',
          '&:hover': {
            backgroundColor: '#C0C0C0', // CharcoalMediumBrush
            borderColor: '#C0C0C0',
          },
          '&:active': {
            backgroundColor: '#E0E0E0', // CharcoalDarkerBrush
          },
          '&:disabled': {
            backgroundColor: 'transparent',
            color: '#666666', // TextSecondaryBrush
            borderColor: '#E0E0E0', // CharcoalDarkerBrush
          },
        },
      },
    },
    // Card/Section Container
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#F5F5F5', // Lighter gray for better contrast
          border: '1px solid #C0C0C0', // CharcoalMediumBrush
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          boxShadow: 'none', // Remove blurry shadows, use border instead
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '0 !important',
          '&:last-child': {
            paddingBottom: '0 !important',
          },
        },
      },
    },
    // Accordion - Sharp borders instead of shadows
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none', // Remove blurry shadows
          border: '1px solid #C0C0C0', // Sharp border
          '&:before': {
            display: 'none', // Remove default divider
          },
        },
      },
    },
    // Input Fields
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#FFFFFF', // White background for better contrast
            borderRadius: '4px',
            '& fieldset': {
              borderColor: '#C0C0C0', // CharcoalMediumBrush
            },
            '&:hover fieldset': {
              borderColor: '#0078D4', // CharcoalAccentBrush on hover
            },
            '&.Mui-focused': {
              backgroundColor: '#FFFFFF', // Keep white when focused
              '& fieldset': {
                borderColor: '#0078D4', // CharcoalAccentBrush
                borderWidth: '2px',
              },
            },
            '&.Mui-disabled': {
              backgroundColor: '#F5F5F5', // Light gray for disabled state
            },
          },
          '& .MuiInputBase-input': {
            fontSize: '13px',
            color: '#000000', // TextPrimaryBrush
            padding: '16.5px 14px',
            minHeight: '1.4375em',
            '&::placeholder': {
              color: '#666666', // Lighter gray for placeholder text
              opacity: 1,
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: '13px',
            color: '#333333', // Dark gray for labels
            '&.MuiInputLabel-shrink': {
              transform: 'translate(14px, -9px) scale(0.75)',
            },
            '&.Mui-focused': {
              color: '#0078D4', // Accent color when focused
            },
          },
          '& .MuiOutlinedInput-notchedOutline': {
            '& legend': {
              maxWidth: '100%',
            },
          },
        },
      },
    },
    // Select Fields - Match TextField styling
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: '4px',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          backgroundColor: '#FFFFFF',
          '& fieldset': {
            borderColor: '#C0C0C0',
          },
          '&:hover fieldset': {
            borderColor: '#0078D4',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#0078D4',
            borderWidth: '2px',
          },
        },
      },
    },
    // AppBar
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#E0E0E0', // CharcoalDarkerBrush - Main window background
          color: '#000000', // TextPrimaryBrush - Black text
          boxShadow: 'none', // Remove blurry shadow
          borderBottom: '1px solid #C0C0C0', // Sharp border instead
        },
      },
    },
    // Paper (used for various surfaces)
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF', // White for better contrast
          boxShadow: 'none', // Remove blurry shadows
          border: '1px solid #C0C0C0', // Sharp border instead
        },
      },
    },
    // List Items
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: '#1F9CF0', // PrimaryMain - Selected items
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#1F9CF0',
            },
          },
        },
      },
    },
    // Tabs
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF', // White for better contrast
          borderBottom: '1px solid #C0C0C0', // CharcoalMediumBrush
        },
        indicator: {
          height: '3px',
          backgroundColor: '#0078D4', // CharcoalAccentBrush
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '0.875rem',
          fontWeight: 500,
          minHeight: '48px',
          color: '#000000', // TextPrimaryBrush
          '&.Mui-selected': {
            color: '#0078D4', // CharcoalAccentBrush
          },
        },
      },
    },
    // Chip
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
        },
      },
    },
    // Typography - Ensure all text is dark for better readability
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.MuiTypography-body2': {
            color: '#333333', // Darker gray for body2 text
          },
        },
      },
    },
  },
});

// Wrapper component to use useNavigate hook
function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | undefined>(() => {
    // Initialize from localStorage on app start
    return localStorage.getItem('selectedOrganizationId') || undefined;
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const login = async (email: string, password: string): Promise<{ success: boolean; message?: string; requiresVerification?: boolean }> => {
    const startTime = new Date().toISOString();
    
    try {
      const response = await api.login(email, password);
      if (response.success) {
        setUser(response.data.user);
        localStorage.setItem('token', response.data.token);
        setSnackbar({ open: true, message: 'Login successful!', severity: 'success' });
        
        // Track successful login
        firebaseAnalytics.trackLogin('email');
        firebaseAnalytics.trackAuthEvent('login', response.data.user.id);
        
        return { success: true };
      } else {
        // Check for specific error cases
        if (response.requiresVerification) {
          return { 
            success: false, 
            message: response.message || 'Email verification required',
            requiresVerification: true 
          };
        }
        
        setSnackbar({ open: true, message: response.message || 'Login failed', severity: 'error' });
        
        // Track failed login with detailed context
        firebaseAnalytics.trackAuthError(
          response.message || 'Login failed', 
          'AUTH_FAILED',
          response, // Pass the full response object for debugging
          {
            email: email,
            action: 'login_attempt',
            timestamp: startTime,
          }
        );
        
        return { success: false, message: response.message || 'Login failed' };
      }
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Check if it's an axios error with response data
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.requiresVerification) {
          return { 
            success: false, 
            message: errorData.message || 'Email verification required',
            requiresVerification: true 
          };
        }
        // Show API error message (include dev detail if present)
        const displayMessage = errorData.error || errorData.message || 'Login failed';
        setSnackbar({ open: true, message: displayMessage, severity: 'error' });
        return { success: false, message: displayMessage };
      }
      
      setSnackbar({ open: true, message: 'Network error. Please try again.', severity: 'error' });
      
      // Track login error with full error details and stack trace
      firebaseAnalytics.trackAuthError(
        'Login network error', 
        'NETWORK_ERROR',
        error, // Pass the full error object to capture stack trace
        {
          email: email,
          action: 'login_attempt',
          timestamp: startTime,
        }
      );
      
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  const resendVerification = async (email: string): Promise<boolean> => {
    try {
      const response = await api.resendVerification(email);
      if (response.success) {
        setSnackbar({ open: true, message: 'Verification email sent successfully!', severity: 'success' });
        return true;
      } else {
        setSnackbar({ open: true, message: response.message || 'Failed to send verification email', severity: 'error' });
        return false;
      }
    } catch (error) {
      console.error('Resend verification error:', error);
      setSnackbar({ open: true, message: 'Failed to send verification email. Please try again.', severity: 'error' });
      return false;
    }
  };

  const register = async (email: string, password: string, displayName: string): Promise<boolean> => {
    try {
      const response = await api.registerUser({ email, password, displayName });
      
      if (response.success) {
        setUser(response.data.user);
        localStorage.setItem('token', response.data.token);
        setSnackbar({ 
          open: true, 
          message: response.message || 'Registration successful!', 
          severity: 'success' 
        });
        setNeedsSetup(false);
        setShowRegister(false);
        return true;
      } else {
        setSnackbar({ open: true, message: response.message || 'Registration failed', severity: 'error' });
        return false;
      }
    } catch (error) {
      console.error('Registration error:', error);
      setSnackbar({ open: true, message: 'Network error. Please try again.', severity: 'error' });
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    setSelectedOrganizationId(undefined);
    localStorage.removeItem('token');
    localStorage.removeItem('selectedOrganizationId');
    setSnackbar({ open: true, message: 'Logged out successfully', severity: 'success' });
  };

  const handleOrganizationChange = (organizationId: string) => {
    setSelectedOrganizationId(organizationId);
    // Persist organization selection to localStorage
    localStorage.setItem('selectedOrganizationId', organizationId);
    
    // If we're on an organization-specific route, navigate to the same page in the new organization
    const currentPath = window.location.pathname;
    const orgRouteMatch = currentPath.match(/^\/org\/[^/]+\/(.+)$/);
    
    if (orgRouteMatch) {
      const currentPage = orgRouteMatch[1]; // e.g., "users", "dashboard", etc.
      const newPath = `/org/${organizationId}/${currentPage}`;
      navigate(newPath); // Use React Router navigation instead of window.location
    }
  };

  const checkAuthStatus = async () => {
    try {
      // Check if we have success parameters - if so, skip authentication entirely
      const hasSuccessParams = searchParams.get('success') === 'true' || searchParams.get('canceled') === 'true';
      
      if (hasSuccessParams) {
        // For success parameters, try to get the actual user if token exists
        const token = localStorage.getItem('token');
        if (token) {
          try {
            const response = await api.getProfile();
            if (response.success) {
              setUser(response.data.user);
            } else {
              // Token is invalid, set temporary user to prevent login page
              setUser({ id: 'temp', email: 'temp@example.com', displayName: 'Temporary User', isVerified: true, isActive: true });
            }
          } catch (error) {
            console.error('Auth check error for success params:', error);
            // Set temporary user to prevent login page
            setUser({ id: 'temp', email: 'temp@example.com', displayName: 'Temporary User', isVerified: true, isActive: true });
          }
        } else {
          // No token, set temporary user to prevent login page
          setUser({ id: 'temp', email: 'temp@example.com', displayName: 'Temporary User', isVerified: true, isActive: true });
        }
        setLoading(false);
        return;
      }

      // First check if system needs setup
      const setupResponse = await api.checkSystemSetup();
      if (setupResponse.success && setupResponse.data.needsSetup) {
        setNeedsSetup(true);
        setLoading(false);
        return;
      }
      
      // Check if user is already logged in
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await api.getProfile();
          if (response.success) {
            setUser(response.data.user);
          } else {
            // Only remove token if it's a clear authentication failure
            // Don't remove token on network errors or temporary server issues
            console.warn('Profile check failed, but keeping token for retry');
          }
        } catch (error: any) {
          console.error('Auth check error:', error);
          // Only remove token if it's a clear 401 Unauthorized error
          // Don't remove token on network errors or temporary server issues
          if (error.response?.status === 401) {
            console.warn('Clear authentication failure, removing token');
            localStorage.removeItem('token');
          } else {
            console.warn('Network or server error during auth check, keeping token');
          }
        }
      }
    } catch (error) {
      console.error('Setup check error:', error);
    }
    setLoading(false);
  };

  // Handle success parameters FIRST, before any other logic
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    
    if (success === 'true' || canceled === 'true') {
      // Extract organization ID from URL path for success URLs
      const pathOrgId = window.location.pathname.split('/')[2];
      if (pathOrgId) {
        // Set the organization ID from URL to prevent organization selector override
        setSelectedOrganizationId(pathOrgId);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    // Initialize Firebase Analytics
    firebaseAnalytics.initialize();
    
    // Set up global error handlers
    const handleGlobalError = (event: ErrorEvent) => {
      firebaseAnalytics.trackError(
        'Unhandled JavaScript Error',
        'UNHANDLED_ERROR',
        {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: event.error,
        }
      );
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      firebaseAnalytics.trackError(
        'Unhandled Promise Rejection',
        'UNHANDLED_REJECTION',
        {
          reason: event.reason,
          promise: event.promise,
        }
      );
    };

    // Add global error listeners
    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    checkAuthStatus();
    
    // Cleanup
    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        Loading...
      </Box>
    );
  }

  const isPlatformAdmin = user?.systemRole === 'PLATFORM_ADMIN';

  // Check if we're on a public route that shouldn't show the login form
  const isPublicRoute = location.pathname === '/new' ||
    location.pathname.startsWith('/accept-invitation/') ||
    location.pathname.startsWith('/reset-password/') ||
    location.pathname.startsWith('/verify-email/');

  return (
    <>
      {/* Handle verify-email route FIRST since it doesn't require authentication */}
      <Routes>
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
      </Routes>

      {/* Public routes (no authentication required) */}
      <Routes>
        <Route path="/new" element={<CreateAccount />} />
        <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
      </Routes>

      {/* Main app content */}
      {!user && !searchParams.get('success') && !searchParams.get('canceled') && !isPublicRoute ? (
        // Show login/register forms when no user and no success/cancel parameters and not on public route
        <>
          {needsSetup ? (
            <Register 
              isSetup
              onRegister={register} 
              onSwitchToLogin={() => setShowRegister(false)} 
            />
          ) : showRegister ? (
            <Register 
              onRegister={register} 
              onSwitchToLogin={() => setShowRegister(false)} 
            />
          ) : showForgotPassword ? (
            <ForgotPassword 
              onSwitchToLogin={() => setShowForgotPassword(false)} 
            />
          ) : (
            <Login 
              onLogin={login} 
              onResendVerification={resendVerification}
              onSwitchToRegister={() => setShowRegister(true)} 
              onSwitchToForgotPassword={() => setShowForgotPassword(true)} 
            />
          )}
        </>
      ) : !isPublicRoute ? (
        // Show main app when user is authenticated or when we have success parameters, but NOT on public routes
        <Layout 
          user={user || (searchParams.get('success') || searchParams.get('canceled') ? { id: 'temp', email: 'temp@example.com', displayName: 'Temporary User' } : null)} 
          onLogout={logout}
          selectedOrganizationId={selectedOrganizationId}
          onOrganizationChange={handleOrganizationChange}
        >
          <UserPreferencesProvider>
          <Routes>
            {/* Root: end-user first glance (org dashboard) or platform admin (admin) */}
            <Route path="/" element={
              selectedOrganizationId ? 
                <Navigate to={`/org/${selectedOrganizationId}/dashboard`} replace /> :
                isPlatformAdmin ? 
                  <Navigate to="/admin" replace /> :
                  <NoOrganization />
            } />
            {/* /dashboard: legacy; send to org dashboard or admin */}
            <Route path="/dashboard" element={
              selectedOrganizationId ? 
                <Navigate to={`/org/${selectedOrganizationId}/dashboard`} replace /> :
                isPlatformAdmin ? 
                  <Navigate to="/admin" replace /> :
                  <NoOrganization />
            } />
            {/* Platform admin dashboard (platform-wide) */}
            <Route path="/admin" element={
              isPlatformAdmin ? <DashboardV2 /> :
              selectedOrganizationId ?
                <Navigate to={`/org/${selectedOrganizationId}/dashboard`} replace /> :
                <NoOrganization />
            } />
            <Route path="/users" element={
              isPlatformAdmin ? <Users /> : 
              selectedOrganizationId ? 
                <Navigate to={`/org/${selectedOrganizationId}/users`} replace /> :
                <NoOrganization />
            } />
            <Route path="/organizations" element={
              isPlatformAdmin ? <Organizations /> : 
              selectedOrganizationId ? 
                <Navigate to={`/org/${selectedOrganizationId}/dashboard`} replace /> :
                <NoOrganization />
            } />
            <Route path="/companies" element={
              isPlatformAdmin ? <Companies /> : 
              selectedOrganizationId ? 
                <Navigate to={`/org/${selectedOrganizationId}/dashboard`} replace /> :
                <NoOrganization />
            } />
            <Route path="/accounts" element={
              isPlatformAdmin ? <Accounts /> : 
              selectedOrganizationId ? 
                <Navigate to={`/org/${selectedOrganizationId}/dashboard`} replace /> :
                <NoOrganization />
            } />
            <Route path="/tasks" element={
              isPlatformAdmin ? <Tasks /> : 
              selectedOrganizationId ? 
                <Navigate to={`/org/${selectedOrganizationId}/dashboard`} replace /> :
                <NoOrganization />
            } />
            <Route path="/timesheets" element={
              isPlatformAdmin ? <Timesheets selectedOrganizationId={selectedOrganizationId} /> : 
              selectedOrganizationId ? 
                <Navigate to={`/org/${selectedOrganizationId}/dashboard`} replace /> :
                <NoOrganization />
            } />
            <Route path="/issues" element={
              isPlatformAdmin ? <Issues /> : 
              selectedOrganizationId ? 
                <Navigate to={`/org/${selectedOrganizationId}/dashboard`} replace /> :
                <NoOrganization />
            } />
            <Route path="/subscriptions" element={
              isPlatformAdmin ? <AdminSubscriptions /> : 
              selectedOrganizationId ? 
                <Navigate to={`/org/${selectedOrganizationId}/subscriptions`} replace /> :
                <NoOrganization />
            } />
            <Route path="/stripe-sync" element={
              isPlatformAdmin ? <AdminStripeSync /> : 
              selectedOrganizationId ? 
                <Navigate to={`/org/${selectedOrganizationId}/dashboard`} replace /> :
                <NoOrganization />
            } />
            <Route path="/reference-fx" element={
              isPlatformAdmin ? <AdminReferenceFx /> :
              selectedOrganizationId ?
                <Navigate to={`/org/${selectedOrganizationId}/dashboard`} replace /> :
                <NoOrganization />
            } />
            <Route path="/email-service" element={
              isPlatformAdmin ? <AdminEmailService /> : 
              selectedOrganizationId ? 
                <Navigate to={`/org/${selectedOrganizationId}/dashboard`} replace /> :
                <NoOrganization />
            } />
            <Route path="/settings" element={
              isPlatformAdmin ? <div>Settings (Coming Soon)</div> : 
              selectedOrganizationId ? 
                <Navigate to={`/org/${selectedOrganizationId}/dashboard`} replace /> :
                <NoOrganization />
            } />
            <Route path="/metrics" element={
              isPlatformAdmin ? <Metrics /> : 
              selectedOrganizationId ? 
                <Navigate to={`/org/${selectedOrganizationId}/dashboard`} replace /> :
                <NoOrganization />
            } />
            <Route path="/preferences" element={
              selectedOrganizationId ? 
                <Navigate to={`/org/${selectedOrganizationId}/preferences`} replace /> :
                <UserPreferences user={user} />
            } />
            <Route path="/download/windows" element={<DownloadWindows />} />
            
            {/* Organization-specific routes (for all users) */}
            <Route path="/org/:organizationId/dashboard" element={<OrganizationDashboard />} />
            <Route path="/org/:organizationId/organization" element={<OrganizationOverview />} />
            <Route path="/org/:organizationId/users" element={<Users />} />
            <Route path="/org/:organizationId/settings" element={<OrganizationSettings />} />
            <Route path="/org/:organizationId/subscriptions" element={<Subscriptions selectedOrganizationId={selectedOrganizationId} />} />
            <Route path="/org/:organizationId/team" element={<div>Team Management (Coming Soon)</div>} />
            <Route path="/org/:organizationId/profile" element={<div>Profile (Coming Soon)</div>} />
            <Route path="/org/:organizationId/preferences" element={<UserPreferences user={user} />} />
            <Route path="/org/:organizationId/help" element={<Help />} />
            <Route path="/org/:organizationId/timesheets/calendar" element={
              selectedOrganizationId ? <Timesheets selectedOrganizationId={selectedOrganizationId} view="calendar" /> :
              <NoOrganization />
            } />
            <Route path="/org/:organizationId/timesheets/table" element={
              selectedOrganizationId ? <Timesheets selectedOrganizationId={selectedOrganizationId} view="table" /> :
              <NoOrganization />
            } />
            <Route path="/org/:organizationId/timesheets/approvals" element={
              selectedOrganizationId ? <Approvals /> :
              <NoOrganization />
            } />
            <Route path="/org/:organizationId/report" element={
              selectedOrganizationId ? <Report selectedOrganizationId={selectedOrganizationId} /> :
              <NoOrganization />
            } />
            <Route path="/org/:organizationId/manage" element={
              selectedOrganizationId ? <Manage /> :
              <NoOrganization />
            } />
            <Route path="/org/:organizationId/manage/add-company" element={
              selectedOrganizationId ? <AddCompany /> :
              <NoOrganization />
            } />
            <Route path="/org/:organizationId/manage/add-account" element={
              selectedOrganizationId ? <AddAccount /> :
              <NoOrganization />
            } />
          </Routes>
          </UserPreferencesProvider>
        </Layout>
      ) : null}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

export default App; 